import { FieldErrorProvider } from '@components/v2/contexts/FieldError.context';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box, Stack } from '@mui/material';
import { ReactNode } from 'react';
import LangEnum from '../../../../../lang/LangEnum';
import { useTranslation } from '../../../../../modules/translation/TranslationContext';
import { ObjectLabel } from '../objectScreen.types';
import DetailsFieldRequiredMarker from './DetailsFieldRequiredMarker';
import { DetailsFieldValue } from './DetailsFieldValue';

export const DETAILS_FIELD_LABEL_WIDTH = 102;

interface DetailsFieldProps {
  label: ObjectLabel;
  value?: ReactNode;
  title?: string;
  icon?: ReactNode;
  control?: ReactNode;
  valueAdornment?: ReactNode;
  isRequired?: boolean;
  hasError?: boolean;
  isMuted?: boolean;
  pendencyKey?: string;
  onEdit?: () => void;
}

export const DetailsField = ({
  label,
  value,
  title,
  icon,
  control,
  valueAdornment,
  isRequired,
  hasError,
  isMuted,
  pendencyKey,
  onEdit,
}: DetailsFieldProps) => {
  const { translate } = useTranslation();

  const translatedLabel = translate(label as LangEnum);

  return (
    <Stack
      direction="row"
      data-pendency-key={pendencyKey}
      sx={{ alignItems: 'center', gap: 1, width: '100%' }}
    >
      <Stack
        direction="row"
        sx={{ alignItems: 'center', width: DETAILS_FIELD_LABEL_WIDTH, flexShrink: 0 }}
      >
        <Typography
          variant="micro1"
          noWrap
          title={translatedLabel}
          sx={{ minWidth: 0, color: 'text.secondary' }}
        >
          {translatedLabel}
        </Typography>
        <DetailsFieldRequiredMarker isRequired={isRequired} hasError={hasError} />
      </Stack>
      <Stack direction="row" sx={{ alignItems: 'center', gap: 0.75, flex: 1, minWidth: 0 }}>
        {control ? (
          <FieldErrorProvider hasError={Boolean(hasError)}>
            <Box sx={{ flex: 1, minWidth: 0 }}>{control}</Box>
          </FieldErrorProvider>
        ) : (
          <DetailsFieldValue
            value={value}
            title={title}
            icon={icon}
            hasError={hasError}
            isMuted={isMuted}
            onEdit={onEdit}
          />
        )}
        {valueAdornment}
      </Stack>
    </Stack>
  );
};
